
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { GetStaticProps, GetStaticPropsContext } from 'next';
import { FormEvent, useState } from 'react';
import styled from 'styled-components';
import { I18N_ROUTES } from 'config/routes';
import { COLORS, SHADOWS } from 'config/theme';
import { searchAccounts } from 'shared/api/accounts';
import { Review } from 'shared/api/latestReviews';
import Page from 'shared/components/Page';
import useTranslation from 'shared/hooks/useTranslation';
import { gradeFormatter } from 'shared/utils/numbers';
import { BaseAccount } from 'shared/utils/types';
import getReviews from 'lib/cache/latestReviews/reviews';
import SearchForm from 'modules/findCompany/SearchForm';
import Head from 'next/head';
import NextImage from 'next/image';
import Link from 'next/link';
type FindCompanyPageProps = {
    latestReviews: Review[];
    context: GetStaticPropsContext;
};
const Intro = styled.div `
  background: ${({ theme }) => theme.COLORS.PRIMARY_GRADIENT};
  width: 100%;
  height: 40rem;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;
const Heading = styled.h1 `
  text-align: center;
  max-width: 100rem;
  padding: 2rem;
  color: white;
  line-height: 3rem;
  font-size: 2.8rem;

  ${({ theme }) => theme.MEDIA.XL} {
    line-height: 36px;
    padding: 3.2rem 3.2rem 0 3.2rem;
    font-size: 4.2rem;
    margin-bottom: 2.4rem;
  }
`;
const ResultTitle = styled.h2 `
  font-size: 2.4em;

  ${({ theme }) => theme.MEDIA.XL} {
    font-size: 3.2em;
  }
`;
const Container = styled.div `
  margin: 5rem auto;
  width: 100%;
  padding: 0 2rem;
  box-sizing: border-box;
  max-width: 118rem;
  height: 100%;
  min-height: 50rem;
`;
const AccountList = styled.ul `
  font-size: 1.6rem;
  list-style-type: none;

  a {
    text-decoration: none;
    color: ${COLORS.PRIMARY};
  }

  li {
    display: flex;
    align-items: center;
    height: 10rem;
    margin: 0.8rem 0;
    box-shadow: ${SHADOWS.PRIMARY};
    position: relative;
    transition: background-color 0.3s;

    &:hover {
      cursor: pointer;
      background-color: ${COLORS.ASH};
    }
  }

  ${({ theme }) => theme.MEDIA.SM} {
    font-size: 2rem;
  }

  ${({ theme }) => theme.MEDIA.XL} {
    font-size: 2.6rem;

    li {
      height: 100%;
      margin: 1.6rem 0;
    }
  }
`;
const AccountUrl = styled.div `
  font-weight: 600;
  margin-left: 1.6rem;
  word-break: break-all;
  margin-right: 1.6rem;

  ${({ theme }) => theme.MEDIA.SM} {
    margin-left: 4.8rem;
  }
`;
const LoaderWrapper = styled.div `
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const ImageWrapper = styled.div `
  margin: 0;
  padding: 0;
  height: 100%;
  margin: 0.4rem;
  display: flex;
  align-items: center;

  img {
    object-fit: contain;
    overflow: hidden;
  }

  ${({ theme }) => theme.MEDIA.XL} {
    margin: 1rem;
  }
`;
const MessageText = styled.h2 `
  margin: 2rem 0;
  font-size: 20px;
`;
const ContentWrap = styled.div `
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
`;
const Grade = styled.div `
  padding: 0 2rem;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 120px;

  h4 {
    margin-left: 1rem;
  }
`;
function FindCompanyPage({ latestReviews, context }: FindCompanyPageProps): JSX.Element {
    const { t } = useTranslation('findCompanyPage');
    const [accountList, setAccountList] = useState<BaseAccount[]>();
    const [loading, setLoading] = useState(false);
    async function handleSearch(e: FormEvent<HTMLFormElement>, search: string): Promise<void> {
        e.preventDefault();
        setLoading(true);
        const foundAccounts = await searchAccounts({ phrase: search });
        setAccountList(foundAccounts);
        setLoading(false);
    }
    return (<Page withWidget={false} reviews={latestReviews}>
      <Head>
        <title>{t('titleTag')}</title>
        <meta name="robots" content="noindex"/>
      </Head>
      <main>
        <Intro>
          <Heading>{t('title')}</Heading>
          <SearchForm 
    // eslint-disable-next-line react/jsx-no-bind
    handleSubmit={handleSearch}/>
        </Intro>
        <Container>
          <ResultTitle>
            {t('searchResult')}: {accountList?.length}
          </ResultTitle>
          {loading ? (<LoaderWrapper>
              <NextImage src="/img/shared/star-loader.gif" alt="star-loader" width={200} height={200} lazyBoundary="100px"/>
            </LoaderWrapper>) : (<AccountList>
              {accountList?.length === 0 ? (<MessageText>{t('noResultMessage')}</MessageText>) : (accountList?.map((item) => (<Link key={item.id} href={`${I18N_ROUTES[`${context.locale}`].REVIEWS}/${item.slug}`} passHref>
                    <li>
                      <ImageWrapper>
                        {item.logoUrl ? (<NextImage src={item.logoUrl} alt={item.logoUrl} width={100} height={100}/>) : (<NextImage src="/img/find-company/landscape.svg" alt="image-placeholder" width={100} height={100}/>)}
                      </ImageWrapper>
                      <ContentWrap>
                        <AccountUrl>{item.sanitizedUrl}</AccountUrl>
                        {item.averageGrade && item.averageGrade !== 0 ? (<Grade>
                            <NextImage src="/img/shared/tm-full-star.svg" alt="image-placeholder" width={32} height={32} lazyBoundary="100px"/>
                            <h4>{gradeFormatter({ grade: item?.averageGrade })}</h4>
                          </Grade>) : null}
                      </ContentWrap>
                    </li>
                  </Link>)))}
            </AccountList>)}
        </Container>
      </main>
    </Page>);
}
const getStaticProps: GetStaticProps<FindCompanyPageProps> = async (context: GetStaticPropsContext) => {
    const latestReviews = await getReviews({
        language: context.locale as string,
    });
    return {
        props: { latestReviews, context },
        revalidate: 60,
    };
};
export default FindCompanyPage;

    async function __Next_Translate__getStaticProps__1983b0f97a3__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/find-company',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1983b0f97a3__ as getStaticProps }
  